import React from 'react';
import { Typography, Tooltip } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { EditOutlined } from '@ant-design/icons';
import { Message } from '../../types/chat';
import MentionText from './MentionText';
import { useTranslation } from 'react-i18next';
import { 
  hasFormattedContent, 
  getMessageContent, 
  getFormattedBody, 
  getFormat, 
  processFormattedContent,
  extractUsernameAndMessage,
  hasBeenEdited,
  formatFullDate,
  capitalizeText,
  lowercaseText
} from './utils';

const { Text, Title } = Typography;

interface FormattedContentProps {
  message: Message;
}

// Компонент для отображения индикатора редактирования
const EditedIndicator: React.FC<{timestamp?: string}> = ({ timestamp }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={`${capitalizeText(t('chat.edited'))}: ${timestamp ? formatFullDate(timestamp) : t('chat.earlier')}`}>
      <Text type="secondary" className="edited-indicator">
        <EditOutlined /> <span style={{ fontSize: '0.8em' }}>{lowercaseText(t('chat.edited'))}</span>
      </Text>
    </Tooltip>
  );
};

const FormattedContent: React.FC<FormattedContentProps> = ({ message }) => {
  // Проверяем, было ли сообщение отредактировано
  const isEdited = hasBeenEdited(message);
  const editTimestamp = typeof message.content === 'object' ? message.content.edit_timestamp : undefined;

  const textStyle = { 
    whiteSpace: 'pre-wrap' as const, 
    wordBreak: 'break-word' as const,
    wordWrap: 'break-word' as const,
    maxWidth: '100%'
  };

  if (hasFormattedContent(message)) {
    const format = getFormat(message);
    const formattedBody = getFormattedBody(message);
    
    // If format is HTML, use dangerouslySetInnerHTML with processing
    if (format === 'org.matrix.custom.html') {
      const processedHtml = processFormattedContent(formattedBody);
      
      // Try to extract username and message
      const { username, localUsername, message: extractedMessage } = extractUsernameAndMessage(processedHtml);
      
      // If username was extracted, render with structured format
      if (username) {
        return (
          <div className="formatted-content">
            {/* Отображаем имя в формате Prefix:Username или просто Username */}
            <Text strong style={{ margin: 0, marginBottom: '2px' }}>
              {localUsername ? `${username}:${localUsername}` : username}
            </Text>
            <div className="formatted-html-content" dangerouslySetInnerHTML={{ __html: extractedMessage }} />
            {isEdited && <EditedIndicator timestamp={editTimestamp} />}
          </div>
        );
      }
      
      // Default HTML rendering
      return (
        <div className="formatted-content">
          <div className="formatted-html-content" dangerouslySetInnerHTML={{ __html: processedHtml }} />
          {isEdited && <EditedIndicator timestamp={editTimestamp} />}
        </div>
      );
    }
    
    // For markdown format
    const content = getMessageContent(message);
    return (
      <div className="formatted-content">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
          {processFormattedContent(content)}
        </ReactMarkdown>
        {isEdited && <EditedIndicator timestamp={editTimestamp} />}
      </div>
    );
  }
  
  // Get the plain text content
  const content = getMessageContent(message);
  
  // Try to extract username and message from plain text as well
  const { username, localUsername, message: extractedMessage } = extractUsernameAndMessage(content);
  
  // If username was extracted, render with structured format
  if (username) {
    return (
      <div className="formatted-content">
        {/* Отображаем имя в формате Prefix:Username или просто Username */}
        <Text strong style={{ margin: 0, marginBottom: '2px' }}>
          {localUsername ? `${username}:${localUsername}` : username}
        </Text>
        <Text style={textStyle}>{extractedMessage}</Text>
        {isEdited && <EditedIndicator timestamp={editTimestamp} />}
      </div>
    );
  }
  
  // Check if plain text contains markdown-style checkboxes
  if (content && (content.includes('[ ]') || content.includes('[x]'))) {
    const processedContent = processFormattedContent(content);
    return (
      <div className="formatted-content">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
          {processedContent}
        </ReactMarkdown>
        {isEdited && <EditedIndicator timestamp={editTimestamp} />}
      </div>
    );
  }
  
  // Check if plain text contains markdown-like formatting
  if (content && (
    content.includes('**') || 
    content.includes('*') || 
    content.includes('<del>') || 
    content.includes('</del>')
  )) {
    return (
      <div className="formatted-content">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
          {content}
        </ReactMarkdown>
        {isEdited && <EditedIndicator timestamp={editTimestamp} />}
      </div>
    );
  }
  
  // Default plain text rendering with preserved line breaks and mentions support
  return (
    <div className="formatted-content">
      <MentionText text={content || ''} />
      {isEdited && <EditedIndicator timestamp={editTimestamp} />}
    </div>
  );
};

export default FormattedContent; 